"use client";
import { createContext, ReactNode, useContext, useId } from "react";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  UseControllerReturn,
  UseFormRegisterReturn,
  useWatch,
} from "react-hook-form";
import { cn } from "../../../utils/cn";

export interface FieldController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
}

export interface FormGroupContextValue<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  controller?: UseControllerReturn<TFieldValues, TName>;
  id: string;
  inputProps?: UseFormRegisterReturn<TName>;
  value: any;
}

const FormGroupContext = createContext<FormGroupContextValue<any, any> | null>(
  null,
);

export function useFormGroup() {
  return useContext(FormGroupContext);
}

export interface FormGroupProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  className?: string;
  children: ReactNode;
  controller: FieldController<TFieldValues, TName>;
  inputProps?: UseFormRegisterReturn<TName>;
  "data-testid"?: string;
}

/**
 * A form group context
 */
function FormGroupRoot<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  className,
  children,
  inputProps,
  controller: fieldController,
  "data-testid": dataTestId,
}: FormGroupProps<TFieldValues, TName>) {
  const css = cn(className);
  const id = useId();
  const value = useWatch({
    control: fieldController.control,
    name: fieldController.name,
  });

  const controller = useController({
    control: fieldController.control,
    name: fieldController.name,
  });

  // Auto-generate data-testid from field name if not provided
  const generateTestId = () => {
    if (dataTestId) return dataTestId;
    return `form-group-${fieldController.name.replace(/\./g, "-")}`;
  };

  const testId = generateTestId();

  return (
    <FormGroupContext.Provider value={{ inputProps, id, controller, value }}>
      <div className={css} data-testid={testId}>
        {children}
      </div>
    </FormGroupContext.Provider>
  );
}

export const FormGroup = Object.assign(FormGroupRoot, {});
